import { isAxiosError } from 'axios';
import { z } from 'zod';
import { SERVER_ERROR_CODES } from '~/generated/server-error-codes';

// A listed rejection clears the session immediately; an unlisted one stays transient until the clear deadline. The allowlist controls how quickly the session clears, not whether it clears.
export const SESSION_REJECTED_STATUS_CODES = [
  SERVER_ERROR_CODES.COMMON_TOKEN_INVALID,
] as const;

type RefreshFailure =
  | { reason: 'rejected'; statusCode: number }
  | { reason: 'transient'; cause: unknown };

export const classifyRefreshStatusCode = (
  statusCode: number | null,
): RefreshFailure => {
  if (
    typeof statusCode === 'number' &&
    SESSION_REJECTED_STATUS_CODES.some((code) => code === statusCode)
  ) {
    return { reason: 'rejected', statusCode };
  }

  return { reason: 'transient', cause: statusCode };
};

export const classifyRefreshFailure = (cause: unknown): RefreshFailure => {
  if (!(isAxiosError(cause) && cause.response)) {
    return { reason: 'transient', cause };
  }

  const { status, data } = cause.response;
  if (status >= 500) {
    return { reason: 'transient', cause };
  }

  if (status !== 401 && status !== 403) {
    return { reason: 'transient', cause };
  }

  const parsed = z.object({ statusCode: z.number() }).safeParse(data);
  if (!parsed.success) {
    return { reason: 'transient', cause };
  }

  if (!SESSION_REJECTED_STATUS_CODES.includes(parsed.data.statusCode)) {
    console.warn(
      `Unexpected refresh rejection status code: ${parsed.data.statusCode}`,
    );
  }

  return classifyRefreshStatusCode(parsed.data.statusCode);
};
